import { Loading } from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspaceMembersService } from '@affine/core/modules/permissions';
import type { Member } from '@affine/core/modules/permissions/entities/members';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSettingsOfflineIncompleteLabel } from '../../shared/settings-offline-status';
import {
  SettingsEntityCard,
  SettingsEntityList,
  SettingsEntityMessage,
} from '../shared/settings-entity';
import * as entityStyles from '../shared/settings-entity.css';
import { SettingsPersonSummary } from '../shared/settings-identity';
import * as styles from './styles.css';
import { UserDocsList } from './user-docs-list';

interface UserListViewProps {
  searchQuery: string;
}

export const UserListView = ({ searchQuery }: UserListViewProps) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const membersService = useService(WorkspaceMembersService);
  const members = useLiveData(membersService.members.pageMembers$);
  const isLoading = useLiveData(membersService.members.isLoading$);
  const error = useLiveData(membersService.members.error$);

  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!backendAvailable) return;
    // Reset to page 0 and load members
    membersService.members.setPageNum(0);
    membersService.members.revalidate();
  }, [backendAvailable, membersService]);

  // Filter members by search query
  const filteredMembers = useMemo(() => {
    if (!members) return [];
    if (!searchQuery.trim()) return members;
    const query = searchQuery.toLowerCase();
    return members.filter(
      member =>
        member.name?.toLowerCase().includes(query) ||
        member.email?.toLowerCase().includes(query)
    );
  }, [members, searchQuery]);

  const handleUserClick = useCallback((userId: string) => {
    setExpandedUserId(prev => (prev === userId ? null : userId));
  }, []);

  const showOfflineNotice =
    !backendAvailable || (error !== null && isBackendUnavailableError(error));

  if (isLoading && !members?.length && !showOfflineNotice) {
    return (
      <SettingsEntityMessage>
        <Loading size={20} />
        <span>
          {t[
            'com.streamlain.settings.workspace.permissions.loading-users'
          ]?.() ?? 'Loading users...'}
        </span>
      </SettingsEntityMessage>
    );
  }

  if (error) {
    return (
      <SettingsEntityMessage>
        {showOfflineNotice
          ? offlineLabel
          : (t[
              'com.streamlain.settings.workspace.permissions.load-error'
            ]?.() ?? 'Failed to load users')}
      </SettingsEntityMessage>
    );
  }

  if (showOfflineNotice && !members?.length) {
    return <SettingsEntityMessage>{offlineLabel}</SettingsEntityMessage>;
  }

  if (filteredMembers.length === 0) {
    return (
      <SettingsEntityMessage>
        {searchQuery
          ? (t[
              'com.streamlain.settings.workspace.permissions.no-users-found'
            ]?.() ?? 'No users found matching your search.')
          : (t['com.streamlain.settings.workspace.permissions.no-users']?.() ??
            'No members in this workspace.')}
      </SettingsEntityMessage>
    );
  }

  return (
    <SettingsEntityList>
      {filteredMembers.map(member => (
        <UserListItem
          key={member.id}
          member={member}
          isExpanded={expandedUserId === member.id}
          onClick={() => handleUserClick(member.id)}
        />
      ))}
    </SettingsEntityList>
  );
};

interface UserListItemProps {
  member: Member;
  isExpanded: boolean;
  onClick: () => void;
}

const UserListItem = ({ member, isExpanded, onClick }: UserListItemProps) => {
  const t = useI18n();

  return (
    <SettingsEntityCard>
      <SettingsPersonSummary
        expanded={isExpanded}
        onToggle={onClick}
        person={member}
        fallbackName={t['Unknown']?.() ?? 'Unknown'}
        badges={
          <span className={styles.badge}>
            {t[
              `com.streamlain.settings.workspace.permissions.role.${member.permission.toLowerCase()}`
            ]?.() ?? member.permission}
          </span>
        }
      />
      {isExpanded && (
        <div className={entityStyles.expanded}>
          <UserDocsList
            userId={member.id}
            userName={member.name || member.email || 'User'}
            userPermission={member.permission}
          />
        </div>
      )}
    </SettingsEntityCard>
  );
};
